import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useToastStore } from '../../lib/toast.js';
import type { ToastVariant } from '../../lib/toast.js';

const ICON_MAP: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const COLOR_MAP: Record<ToastVariant, string> = {
  success: 'var(--color-success)',
  error: 'var(--color-danger)',
  info: 'var(--color-info)',
  warning: 'var(--color-warning)',
};

export default function Toaster() {
  const { toasts, dismiss } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        bottom: 'var(--space-6)',
        right: 'var(--space-6)',
        zIndex: 'var(--z-toast)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => {
        const Icon = ICON_MAP[toast.variant];
        const color = COLOR_MAP[toast.variant];

        return (
          <div
            key={toast.id}
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              background: 'var(--color-white)',
              border: '1px solid var(--color-gray-100)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3) var(--space-4)',
              boxShadow: 'var(--shadow-lg)',
              pointerEvents: 'all',
              minWidth: 280,
              maxWidth: 400,
              animation: 'toast-in 240ms var(--ease-out)',
            }}
          >
            <span style={{ color, display: 'flex', flexShrink: 0 }}>
              <Icon size={18} />
            </span>
            <span
              style={{
                flex: 1,
                fontSize: 'var(--text-sm)',
                color: 'var(--color-ink)',
              }}
            >
              {toast.message}
            </span>
            {toast.action && (
              <button
                onClick={toast.action.onClick}
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-brand)',
                  fontWeight: 'var(--font-medium)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-gray-400)',
                padding: 0,
                display: 'flex',
              }}
            >
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
